/**
 * Phase 86 Plan 86-04 Task 3 — the Compost design's footer band
 * (`.planning/design/mytasks-compost/README.md`, "Footer band — FREE TO
 * GRAB"), extended after Petra UAT 2026-08-18.
 *
 * The handoff's band reported a count and an age, which tells you unclaimed
 * work exists but nothing about whether any of it is worth your next hour —
 * so it read as a notification to dismiss. It now offers up to three concrete
 * tasks, each answering a different "what have I got the head for": something
 * small, something under pressure, something nobody has rescued.
 *
 * Every card states a checkable fact about the task (an estimate, an SLA
 * percentage, a number of days). None of them claims the task suits the
 * reader: a task carries no role or skill in this schema, so that claim would
 * be invented. `HAVE A LOOK` still reveals the full claimable list.
 *
 * Self-contained: reads the SAME `useUnassignedTasks` query `PickSomethingUp`
 * reads (identical query key + queryFn), so mounting this band adds no
 * network request — TanStack Query's cache serves both from one fetch.
 */
import { useMemo } from 'react';
import { useUnassignedTasks } from '../../hooks/useUnassignedTasks';
import { useClaimTask } from '../../hooks/useClaimTask';
import { pickFreeToGrab, type FreeToGrabPick, type FreeToGrabPickKind } from '../../utils/myTasksCompost';
import {
  MICRO_LABEL_GREY_HEX,
  FRESH_HEX,
  FRESH_DARK_HEX,
  STALE_DARK_HEX,
  STALE_HEX,
  GRAVEYARD_DARK_HEX,
  GRAVEYARD_BORDER_HEX,
} from './compostTokens';

interface CardStyle {
  label: string;
  /** Heading + claim-button text: the darker, text-safe end of the zone pair. */
  accent: string;
  /** Card and button border: the brighter end, used for outline only. */
  border: string;
}

/** Each card borrows its zone's colour pair, so a card reads as the same
 * kind of thing as the band it would land under once claimed. */
const CARD_STYLE: Record<FreeToGrabPickKind, CardStyle> = {
  'quick-win': { label: 'QUICK WIN', accent: FRESH_DARK_HEX, border: FRESH_HEX },
  'on-fire': { label: 'ON FIRE', accent: STALE_DARK_HEX, border: STALE_HEX },
  orphan: { label: 'ORPHAN', accent: GRAVEYARD_DARK_HEX, border: GRAVEYARD_BORDER_HEX },
};

export interface MyTasksFreeToGrabProps {
  onOpen: () => void;
}

function PickCard({ pick }: { pick: FreeToGrabPick }) {
  const style = CARD_STYLE[pick.kind];
  const claim = useClaimTask();
  const code = pick.task.taskCode ?? `#${pick.task.taskId}`;

  return (
    <div
      data-testid={`free-to-grab-card-${pick.kind}`}
      className="flex min-w-0 flex-1 flex-col gap-[6px] rounded-[6px] border bg-ms-surface p-3"
      style={{ borderColor: style.border }}
    >
      <span
        className="font-mono text-[9px] font-bold uppercase tracking-[0.16em]"
        style={{ color: style.accent }}
      >
        {style.label}
      </span>
      <span className="font-mono text-[10.5px]" style={{ color: MICRO_LABEL_GREY_HEX }}>
        {code}
      </span>
      <span className="truncate text-[12.5px] font-medium text-ms-text-strong" title={pick.task.taskName}>
        {pick.task.taskName}
      </span>
      <span className="text-[10.5px]" style={{ color: MICRO_LABEL_GREY_HEX }}>
        {pick.task.projectName} · {pick.reason}
      </span>
      <button
        type="button"
        onClick={() => claim.mutate(pick.task.taskId)}
        disabled={claim.isPending}
        data-testid={`free-to-grab-claim-${pick.kind}`}
        aria-label={`Claim ${code}`}
        className="mt-1 self-start rounded border px-[10px] py-[5px] font-mono text-[10px] font-bold disabled:opacity-50"
        style={{ borderColor: style.border, color: style.accent }}
      >
        {claim.isPending ? 'CLAIMING…' : 'CLAIM'}
      </button>
      {claim.isError && (
        <span className="text-[10.5px] text-red-600" role="alert">
          Could not claim it — someone may have taken it first.
        </span>
      )}
    </div>
  );
}

export function MyTasksFreeToGrab({ onOpen }: MyTasksFreeToGrabProps) {
  const { data } = useUnassignedTasks();
  const tasks = useMemo(() => data ?? [], [data]);
  const picks = useMemo(() => pickFreeToGrab(tasks), [tasks]);
  const count = tasks.length;

  return (
    <div
      data-testid="my-tasks-free-to-grab"
      className="flex flex-col gap-3 px-[18px] py-[14px] shrink-0"
      style={{ background: 'var(--ms-surface-2)' }}
    >
      <div className="flex items-center gap-3">
        <span
          className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] shrink-0"
          style={{ color: MICRO_LABEL_GREY_HEX }}
        >
          FREE TO GRAB · {count}
        </span>
        <span className="text-[11.5px]" style={{ color: MICRO_LABEL_GREY_HEX }}>
          {count === 0
            ? 'Nothing unassigned on your projects right now.'
            : picks.length === 1
              ? 'One worth a look on your projects.'
              : 'Three angles on what is unclaimed on your projects.'}
        </span>
        {count > 0 && (
          <button
            type="button"
            onClick={onOpen}
            data-testid="my-tasks-free-to-grab-open"
            className="ml-auto px-[11px] py-[6px] rounded border border-ms-border bg-ms-surface font-mono text-[10.5px] font-semibold"
            style={{ color: 'var(--ms-text-secondary)' }}
          >
            HAVE A LOOK
          </button>
        )}
      </div>

      {picks.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {picks.map((pick) => (
            <PickCard key={pick.task.taskId} pick={pick} />
          ))}
        </div>
      )}
    </div>
  );
}
